// client/src/components/bank/VatCreditBidDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatInterestRate, formatCurrencyWithSymbol, formatBaseRateDisplay } from '../utils/formatUtils';
import { Info } from 'lucide-react';
import VatCreditAttachmentList from '../vatCredit/VatCreditAttachmentList';
import {
  formatCreditTypeAndPeriod,
  formatMaturity,
  formatCoverage,
  formatRequestedRateTypes,
  formatDayCountBasis,
  formatOptionalDate,
  formatCostAmount
} from '../vatCredit/vatCreditUtils';

const VatCreditBidDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [bid, setBid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Helper function to format base rate
  const formatBaseRate = (baseRate) => {
    return formatBaseRateDisplay(baseRate, t);
  };

  useEffect(() => {
    const fetchBidDetails = async () => {
      try {
        // Non esiste un endpoint dedicato: recupera tutte le offerte della banca e filtra
        const bidsRes = await axios.get('/api/vat-credit-bids/bank');

        const foundBid = (bidsRes.data.bids || []).find(b => b.id === id);

        if (!foundBid) {
          setError(t('common.bidNotFound'));
          setLoading(false);
          return;
        }

        setBid(foundBid);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching VAT credit bid details:');
        setError(t('common.errors.fetchBidDetails'));
        setLoading(false);
      }
    };

    fetchBidDetails();
  }, [id, t]);

  const cancelBid = async () => {
    if (window.confirm(t('bids.bidCancelConfirm'))) {
      try {
        await axios.post(`/api/vat-credit-bids/cancel/${id}`);

        // Refresh bid data after cancellation
        const bidsRes = await axios.get('/api/vat-credit-bids/bank');
        const updatedBid = (bidsRes.data.bids || []).find(b => b.id === id);
        setBid(updatedBid);

        setSuccessMessage(t('bids.bidCancelledSuccess'));

        setTimeout(() => {
          setSuccessMessage('');
          navigate('/bank/vat-credit/open-requests');
        }, 3000);
      } catch (err) {
        console.error('Error cancelling VAT credit bid:');
        setError(t('common.errors.cancelBid') + ': ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const getStatusClass = () => {
    if (bid.status === 'approved') {
      return 'status-approved';
    } else if (bid.status === 'rejected') {
      return 'status-rejected';
    } else if (bid.status === 'cancelled') {
      return 'status-cancelled';
    } else {
      return 'status-pending';
    }
  };

  const getBackUrl = () => {
    return '/bank/vat-credit/past-bids';
  };

  const getBackButtonText = () => {
    if (bid.status === 'pending') {
      return t('bids.backToPendingBids');
    }
    return t('bids.backToPastBids');
  };

  // Helper function to safely get allocation amount
  const getAllocationAmount = () => {
    if (bid.allocation && typeof bid.allocation.amount !== 'undefined') {
      return bid.allocation.amount;
    } else if (typeof bid.allocatedAmount !== 'undefined') {
      return bid.allocatedAmount;
    } else {
      return 0;
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loadingBidDetails')}</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!bid) {
    return <div className="error-message">{t('common.bidNotFound')}</div>;
  }

  const currency = bid.request?.currency || 'EUR';

  return (
    <div className="bid-detail company-dashboard">
      {/* Navigation button outside and above the card */}
      <div className="page-navigation" style={{ marginBottom: '14px' }}>
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate(getBackUrl(), bid.status === 'pending' ? { state: { activeTab: 'pending' } } : undefined)}
        >
          ← {getBackButtonText()}
        </button>
      </div>

      <div className="card">
        <div className="card-title">
          <span>
            <Info className="card-title-icon" size={20} style={{ marginRight: '8px' }} />
            {t('vatCredit.bidDetails')}
          </span>
          <div>
            <span className={`status ${getStatusClass()}`}>
              {bid.status === 'pending' ? t('bids.pending') :
               bid.status === 'approved' ? t('bids.approved') :
               bid.status === 'rejected' ? t('bids.rejected') :
               t('bids.cancelled')}
            </span>
          </div>
        </div>

        {/* Display success message if available */}
        {successMessage && (
          <div className="alert-success" style={{ marginTop: '15px', marginBottom: '15px' }}>
            <p>{successMessage}</p>
          </div>
        )}

        <div className="bid-info">
          {/* Primo box - Richiedente con ID e data */}
          <div className="allocation-details-card">
            <div style={{ display: 'flex', gap: '40px', justifyContent: 'center' }}>
              <div>
                <strong>{t('requests.requestId')}:</strong> {bid.request?.requestId ? `#${bid.request.requestId}` : t('bids.notAvailable')}
              </div>
              <div>
                <strong>{t('requests.company')}:</strong> {bid.request?.company?.name || t('bids.notAvailable')}
              </div>
              <div>
                <strong>{t('profile.vatNumber')}:</strong> {bid.request?.company?.vatNumber || t('profile.vatNumberNotSpecified')}
              </div>
              <div>
                <strong>{t('bids.submissionDate')}:</strong> {new Date(bid.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* Secondo box - Dettagli della richiesta e dell'offerta */}
          <div className="bank-bid detail-list-lined" style={{ marginBottom: '25px', marginTop: '20px' }}>
            <h3 style={{ fontSize: '20px' }}>{t('vatCredit.requestDetails')}</h3>

            <div className="detail-item">
              <div className="detail-label">{t('requests.currency')}:</div>
              <div className="detail-value">{currency}</div>
            </div>

            {bid.request?.creditType && (
              <>
                <div className="detail-item">
                  <div className="detail-label">{t('vatCredit.creditPeriod')}:</div>
                  <div className="detail-value">{formatCreditTypeAndPeriod(bid.request, t)}</div>
                </div>

                <div className="detail-item">
                  <div className="detail-label">{t('vatCredit.coverage')}:</div>
                  <div className="detail-value">{formatCoverage(bid.request, t)}</div>
                </div>
              </>
            )}

            <div className="detail-item">
              <div className="detail-label">{t('vatCredit.amountToAssign')}:</div>
              <div className="detail-value">
                {bid.request?.amount ? formatCurrencyWithSymbol(bid.request.amount, currency) : t('bids.notAvailable')}
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-label">{t('vatCredit.maturityDate')}:</div>
              <div className="detail-value">{formatMaturity(bid.request, t)}</div>
            </div>

            {bid.request?.requestedRateTypes?.length > 0 && (
              <div className="detail-item">
                <div className="detail-label">{t('vatCredit.requestedRateType')}:</div>
                <div className="detail-value">{formatRequestedRateTypes(bid.request, t)}</div>
              </div>
            )}

            {bid.request?.bidDeadline && (
              <div className="detail-item">
                <div className="detail-label">{t('vatCredit.bidDeadline')}:</div>
                <div className="detail-value">
                  {new Date(bid.request.bidDeadline).toLocaleDateString()}
                  {' '}
                  <span style={{ color: '#6c757d' }}>
                    {new Date(bid.request.bidDeadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} CET
                  </span>
                </div>
              </div>
            )}

            {bid.request?.executionDate && (
              <div className="detail-item">
                <div className="detail-label">{t('vatCredit.executionDate')}:</div>
                <div className="detail-value">{new Date(bid.request.executionDate).toLocaleDateString()}</div>
              </div>
            )}

            <h3 style={{ fontSize: '20px', marginTop: '20px' }}>{t('bids.bidInformation')}</h3>

            <div className="detail-item">
              <div className="detail-label">{t('vatCredit.maxAmount')}:</div>
              <div className="detail-value">{formatCurrencyWithSymbol(bid.desiredAmount, currency)}</div>
            </div>

            <div className="detail-item">
              <div className="detail-label">{t('vatCredit.bidRateType')}:</div>
              <div className="detail-value">{t(`vatCredit.bidRateTypes.${bid.rateType}`)}</div>
            </div>

            <div className="detail-item">
              <div className="detail-label">
                {bid.rateType === 'floating' ? t('bids.interestRateFloating').replace(' (%)', '') + ':' : t('bids.interestRate') + ':'}
              </div>
              <div className="detail-value">{formatInterestRate(bid.interestRate, bid.rateType, bid.baseRate)}</div>
            </div>

            {bid.rateType === 'floating' && bid.baseRate && (
              <div className="detail-item">
                <div className="detail-label">{t('bids.baseRate')}:</div>
                <div className="detail-value">{formatBaseRate(bid.baseRate)}</div>
              </div>
            )}

            <div className="detail-item">
              <div className="detail-label">{t('vatCredit.dayCountBasis')}:</div>
              <div className="detail-value">{formatDayCountBasis(bid, t) || t('bids.notAvailable')}</div>
            </div>

            <div className="detail-item">
              <div className="detail-label">{t('vatCredit.officialMaturityDate')}:</div>
              <div className="detail-value">{formatOptionalDate(bid.officialMaturityDate, t)}</div>
            </div>

            <div className="detail-item">
              <div className="detail-label">{t('vatCredit.commission')}:</div>
              <div className="detail-value">
                {bid.commission !== undefined && bid.commission !== null
                  ? `${bid.commission}%`
                  : t('bids.notAvailable')}
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-label">{t('vatCredit.fees')}:</div>
              <div className="detail-value">
                {bid.fees !== undefined && bid.fees !== null
                  ? formatCurrencyWithSymbol(bid.fees, currency)
                  : t('bids.notAvailable')}
              </div>
            </div>

            {/* Bank Notes */}
            {bid.notes && (
              <div className="detail-item">
                <div className="detail-label">{t('requests.additionalNotes')}:</div>
                <div className="detail-value">{bid.notes}</div>
              </div>
            )}

            {/* Created By - ultimo campo */}
            <div className="detail-item">
              <div className="detail-label">{t('bids.createdBy')}:</div>
              <div className="detail-value">{bid.createdBy ? `${bid.createdBy.firstName} ${bid.createdBy.lastName}` : t('bids.notAvailable')}</div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '20px' }}>
          <VatCreditAttachmentList
            requestId={bid.request?._id || bid.request?.id}
            attachments={bid.request?.attachments}
          />
        </div>

        {/* Alert based on bid status */}
        {bid.status === 'pending' && (
          <div className="alert-info">
            <p>{t('bids.bidPendingReview')}</p>
          </div>
        )}

        {bid.status === 'rejected' && (
          <div className="alert-danger">
            <p>{t('bids.bidRejectedMessage')}</p>
          </div>
        )}

        {bid.status === 'cancelled' && (
          <div className="alert-danger">
            <p>{t('bids.bidCancelledMessage')}</p>
          </div>
        )}

        {bid.status === 'approved' && (
          <div className="alert-success">
            <p>{t('bids.bidApprovedMessage')}</p>
          </div>
        )}

        {/* Allocation details for approved bids */}
        {bid.status === 'approved' && (
          <div className="bank-bid detail-list-lined" style={{ marginBottom: '25px', marginTop: '20px' }}>
            <h3 style={{ marginBottom: '10' }}>{t('bids.allocationDetails')}</h3>
            <div className="detail-item">
              <div className="detail-label">{t('bids.allocatedAmount')}:</div>
              <div className="detail-value">{formatCurrencyWithSymbol(getAllocationAmount(), currency)}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">
                {bid.rateType === 'floating' ? t('bids.interestRateFloating').replace(' (%)', '') + ':' : t('bids.interestRate') + ':'}
              </div>
              <div className="detail-value">{formatInterestRate(bid.interestRate, bid.rateType, bid.baseRate)}</div>
            </div>
            {bid.rateType === 'floating' && bid.baseRate && (
              <div className="detail-item">
                <div className="detail-label">{t('bids.baseRate')}:</div>
                <div className="detail-value">{formatBaseRate(bid.baseRate)}</div>
              </div>
            )}
            <div className="detail-item">
              <div className="detail-label">{t('vatCredit.executionDate')}:</div>
              <div className="detail-value">
                {bid.allocation?.executionDate
                  ? new Date(bid.allocation.executionDate).toLocaleDateString()
                  : t('bids.notAvailable')}
              </div>
            </div>

            {/* Costo calcolato all'allocazione (assente per offerte legacy) */}
            {bid.allocation?.totalCost !== undefined && bid.allocation?.totalCost !== null && (
              <>
                <div className="detail-item">
                  <div className="detail-label">{t('vatCredit.cost.interest')}:</div>
                  <div className="detail-value">
                    {formatCostAmount(bid.allocation.interestAmount, currency)}
                    {' '}
                    <span style={{ color: '#6c757d' }}>
                      ({bid.allocation.effectiveRate.toFixed(3)}% · {bid.allocation.days} {t('common.days')})
                    </span>
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('vatCredit.cost.commission')}:</div>
                  <div className="detail-value">{formatCostAmount(bid.allocation.commissionAmount, currency)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('vatCredit.fees')}:</div>
                  <div className="detail-value">{formatCostAmount(bid.allocation.fees, currency)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('vatCredit.cost.totalCost')}:</div>
                  <div className="detail-value"><strong>{formatCostAmount(bid.allocation.totalCost, currency)}</strong></div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Only show action buttons for pending bids, and only the cancel button */}
        {bid.status === 'pending' && (
          <div className="bid-actions">
            <button
              type="button"
              className="btn btn-danger"
              onClick={cancelBid}
            >
              {t('bids.cancelBid')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VatCreditBidDetail;
